import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for monitoring performance metrics
 */
export const usePerformanceMonitor = (componentName, options = {}) => {
  const {
    enableLogging = false,
    logThreshold = 16.67, // 60fps threshold
    onPerformanceIssue = null
  } = options;

  const renderCount = useRef(0);
  const lastRenderTime = useRef(performance.now());
  const frameCount = useRef(0);
  const lastFrameTime = useRef(performance.now());

  const measureRender = useCallback(() => {
    const now = performance.now();
    const renderTime = now - lastRenderTime.current;
    
    renderCount.current++;
    
    if (renderTime > logThreshold) {
      if (enableLogging) {
        console.warn(`[${componentName}] Slow render detected: ${renderTime.toFixed(2)}ms`);
      }
      
      if (onPerformanceIssue) {
        onPerformanceIssue({
          type: 'slow-render',
          component: componentName,
          renderTime,
          renderCount: renderCount.current
        });
      }
    }
    
    lastRenderTime.current = now;
  }, [componentName, logThreshold, enableLogging, onPerformanceIssue]);

  const measureFrame = useCallback(() => {
    const now = performance.now();
    const frameTime = now - lastFrameTime.current;
    
    frameCount.current++;
    
    if (frameTime > logThreshold) {
      if (enableLogging) {
        console.warn(`[${componentName}] Frame drop detected: ${frameTime.toFixed(2)}ms`);
      }
      
      if (onPerformanceIssue) {
        onPerformanceIssue({
          type: 'frame-drop',
          component: componentName,
          frameTime,
          frameCount: frameCount.current
        });
      }
    }
    
    lastFrameTime.current = now;
  }, [componentName, logThreshold, enableLogging, onPerformanceIssue]);

  // Monitor frame rate
  useEffect(() => {
    let animationFrameId;
    
    const monitorFrames = () => {
      measureFrame();
      animationFrameId = requestAnimationFrame(monitorFrames);
    };
    
    animationFrameId = requestAnimationFrame(monitorFrames);
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [measureFrame]);

  // Monitor render performance
  useEffect(() => {
    measureRender();
  });

  const getMetrics = useCallback(() => ({
    renderCount: renderCount.current,
    frameCount: frameCount.current,
    averageRenderTime: renderCount.current > 0 ? 
      (performance.now() - lastRenderTime.current) / renderCount.current : 0
  }), []);

  return {
    measureRender,
    measureFrame,
    getMetrics
  };
};

export default usePerformanceMonitor;